import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect, useRef } from "react";
import { Customer, Invoice } from "../../../types";

interface PaymentLinkDialogProps {
  open: boolean;
  closeDialog: () => void;
  customer: Customer | null;
  marketId: string;
  invoice?: Invoice | null;
  amount?: number; // This will be the default amount in cents
  currency?: string;
  customerBalanceInCents?: number; // Add this new prop for customer balance
}

export function PaymentLinkDialog({
  open,
  closeDialog,
  customer,
  marketId,
  invoice,
  amount,
  currency = "GBP",
  customerBalanceInCents = 0,
}: PaymentLinkDialogProps) {
  const [copied, setCopied] = useState(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  // Function to generate random alphanumeric string
  const generateRandomString = (length: number) => {
    let chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // State for editable amount - default to customer balance if no specific amount provided
  const [paymentAmountInCents, setPaymentAmountInCents] = useState<number>(0);
  const [amountInput, setAmountInput] = useState<string>("");

  // Initialize amount when dialog opens
  useEffect(() => {
    if (open) {
      let defaultAmount = amount ?? customerBalanceInCents ?? 0;
      // Only use customer balance if it's negative (they owe money)
      if (!amount && customerBalanceInCents < 0) {
        defaultAmount = customerBalanceInCents;
      }
      setPaymentAmountInCents(Math.abs(defaultAmount)); //
      setAmountInput((Math.abs(defaultAmount) / 100).toFixed(2));
    }
  }, [open, amount, customerBalanceInCents]);

  // Handle amount input changes
  const handleAmountChange = (value: string) => {
    setAmountInput(value);

    // Parse the amount and convert to cents
    let parsedAmount = parseFloat(value) || 0;
    setPaymentAmountInCents(Math.round(parsedAmount * 100));
  };

  // Reset copied state and clear any pending timeout when dialog opens/closes
  useEffect(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    // Reset copied state when dialog opens
    if (open) {
      setCopied(false);
    }
    // Cleanup timeout on component unmount or when dialog closes
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [open]);

  // Generate unique payment link
  const generatePaymentLink = () => {
    if (!customer) return "";

    const paymentAmount = (paymentAmountInCents / 100).toFixed(2);
    const reference = invoice
      ? `INVOICE ${invoice.invoiceNumber} ${invoice.id.slice(0, 6)}`
      : `PAYMENT ${customer.accountNumber} ${generateRandomString(6)}`;

    // Return direct PitchPay link
    return `https://pitchpay.me/${marketId}/${paymentAmount}?d=${encodeURIComponent(
      reference
    )}`;
  };

  const paymentLink = generatePaymentLink();

  const copyToClipboard = async () => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    if (!navigator.clipboard) {
      alert("Clipboard API not available. Please copy manually.");
      return;
    }

    try {
      await navigator.clipboard.writeText(paymentLink);
      setCopied(true);

      timeoutIdRef.current = setTimeout(() => {
        setCopied(false);
        timeoutIdRef.current = null;
      }, 2000);
    } catch (err) {
      console.error(
        "[PitchPayDialog] navigator.clipboard.writeText FAILED:",
        err
      );
      // If copy fails, ensure `copied` is false so the button doesn't show "Copied!"
      setCopied(false);
      if (timeoutIdRef.current) {
        // Clear any potentially running timeout if an error occurs
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      alert("Failed to copy link. Please try again or copy manually.");
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeDialog}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mx-auto flex h-16 w-48 items-center justify-center mb-4">
                    <svg
                      className="h-full w-full"
                      width="363"
                      height="111"
                      viewBox="0 0 363 111"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M333.557 75.1505C333.4 75.1505 329.659 75.1505 329.502 75.1191V66.6944C329.659 66.7258 331.734 66.7572 331.922 66.7572C335.412 66.7572 337.078 65.877 337.361 63.8023C337.361 63.6137 337.486 62.7649 337.424 62.5449L325.887 28.3115H338.178L344.371 54.183H344.559L350.721 28.3115H362.478L351.538 62.4506C348.615 71.6297 343.585 75.1505 333.557 75.1505Z"
                        fill="#00E9A8"
                      />
                      <path
                        d="M306.06 55.3834C309.455 55.3834 312.41 53.3086 312.41 50.2594V47.9017L306.186 48.3103C303.262 48.5304 301.628 49.8821 301.628 51.894C301.628 54.0631 303.419 55.3834 306.06 55.3834ZM302.099 63.0536C295.749 63.0536 290.594 58.8413 290.594 52.6485C290.594 46.2356 295.529 42.6519 304.394 42.1175L312.41 41.646V39.697C312.41 36.9621 310.461 35.4532 307.538 35.4532C304.52 35.4532 302.602 37.025 302.288 39.1312H292.26C292.574 32.121 298.547 27.5 308.103 27.5C317.251 27.5 323.318 32.1839 323.318 39.0683V62.5507H312.473V57.6781H312.253C310.398 61.1361 306.217 63.0536 302.099 63.0536Z"
                        fill="#00E9A8"
                      />
                      <path
                        d="M253.395 17.1855H273.042C282.787 17.1855 289.388 23.5041 289.388 33.2491C289.388 42.9313 282.441 49.2498 272.35 49.2498H264.931V62.5471H253.395V17.1855ZM264.931 26.0189V40.5736H269.93C274.834 40.5736 277.726 38.0273 277.726 33.2806C277.726 28.5652 274.834 26.0189 269.993 26.0189H264.931Z"
                        fill="#00E9A8"
                      />
                      <path
                        d="M213.652 62.5471V17.1855H224.56V34.758H224.781C226.541 30.1684 230.282 27.6536 235.5 27.6536C243.139 27.6536 247.383 32.4947 247.383 40.5736V62.5471H236.255V42.9941C236.255 39.1276 234.337 36.7385 230.659 36.7385C226.918 36.7385 224.812 39.4105 224.812 43.2456V62.5471H213.652Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M209.35 41.8346H199.102C198.725 38.2824 196.587 35.9876 193.349 35.9876C189.42 35.9876 187.125 39.3826 187.125 45.4183C187.125 51.5168 189.42 54.9118 193.349 54.9118C196.619 54.9118 198.725 52.8056 199.134 49.2534H209.35C209.067 57.9611 202.874 63.368 193.224 63.368C182.504 63.368 175.84 56.6408 175.84 45.4183C175.84 34.3215 182.504 27.5 193.161 27.5C202.812 27.5 209.067 33.2527 209.35 41.8346Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M155.219 20.6455H166.378V28.3158H172.54V36.5205H166.378V50.8865C166.378 53.2442 167.636 54.4387 170.308 54.4387C171.188 54.4387 171.848 54.4073 172.508 54.2816V62.2033C171.44 62.4234 169.805 62.612 167.636 62.612C158.74 62.612 155.219 59.8771 155.219 53.1499V36.5205H150.566V28.3158H155.219V20.6455Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M135.852 62.5484V28.3151H146.981V62.5484H135.852ZM141.417 25.5488C138.179 25.5488 135.695 23.1597 135.695 20.079C135.695 16.9983 138.179 14.6406 141.417 14.6406C144.654 14.6406 147.106 16.9983 147.106 20.079C147.106 23.1597 144.654 25.5488 141.417 25.5488Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M95.3984 17.1855H115.046C124.791 17.1855 131.392 23.5041 131.392 33.2491C131.392 42.9313 124.445 49.2498 114.354 49.2498H106.935V62.5471H95.3984V17.1855ZM106.935 26.0189V40.5736H111.934C116.837 40.5736 119.73 38.0273 119.73 33.2806C119.73 28.5652 116.837 26.0189 111.996 26.0189H106.935Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M266.951 88.232C264.307 88.232 262.45 90.2461 262.257 93.0309H271.47C271.382 90.2111 269.596 88.232 266.951 88.232ZM271.452 98.5303H275.043C274.517 101.893 271.33 104.292 267.109 104.292C261.697 104.292 258.422 100.632 258.422 94.8173C258.422 89.0377 261.75 85.1846 266.951 85.1846C272.065 85.1846 275.253 88.7925 275.253 94.3795V95.6755H262.24V95.9032C262.24 99.1083 264.167 101.227 267.196 101.227C269.351 101.227 270.944 100.142 271.452 98.5303Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M242.328 110.801C242.03 110.801 241.067 110.766 240.786 110.714V107.649C241.067 107.719 241.697 107.719 242.012 107.719C243.781 107.719 244.709 106.983 245.287 105.092C245.287 105.056 245.62 103.971 245.62 103.953L238.965 85.5107H243.063L247.739 100.503H247.792L252.468 85.5107H256.461L249.543 104.899C247.967 109.365 246.163 110.801 242.328 110.801Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M235.863 100.537V103.935H219.82V78.6621H235.863V82.0423H223.743V89.4333H235.215V92.6909H223.743V100.537H235.863Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M207.863 81.1533H211.664V85.5143H215.167V88.5092H211.664V98.6674C211.664 100.261 212.364 100.979 213.906 100.979C214.291 100.979 214.904 100.944 215.149 100.892V103.887C214.729 103.992 213.888 104.062 213.047 104.062C209.317 104.062 207.863 102.643 207.863 99.0878V88.5092H205.184V85.5143H207.863V81.1533Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M193.926 103.939V85.5145H197.534V88.6496H197.621C198.234 86.4953 199.881 85.2168 202.052 85.2168C202.595 85.2168 203.068 85.3044 203.348 85.3569V88.8772C203.051 88.7546 202.385 88.6671 201.65 88.6671C199.215 88.6671 197.709 90.2959 197.709 92.9055V103.939H193.926Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M180.685 101.263C183.329 101.263 185.396 99.441 185.396 97.0416V95.5879L180.86 95.8682C178.601 96.0258 177.322 97.0241 177.322 98.6004C177.322 100.212 178.653 101.263 180.685 101.263ZM179.634 104.257C176.026 104.257 173.504 102.016 173.504 98.7055C173.504 95.4829 175.973 93.5038 180.352 93.2586L185.396 92.9608V91.5422C185.396 89.493 184.012 88.2671 181.701 88.2671C179.511 88.2671 178.145 89.3179 177.812 90.9642H174.239C174.45 87.6365 177.287 85.1846 181.841 85.1846C186.307 85.1846 189.162 87.549 189.162 91.2444V103.942H185.536V100.912H185.449C184.38 102.961 182.051 104.257 179.634 104.257Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M169.461 103.935H165.871V85.335H165.748L158.13 103.742H155.222L147.586 85.335H147.464V103.935H143.891V78.6621H148.427L156.606 98.6107H156.746L164.908 78.6621H169.461V103.935Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M117.624 110.801C117.327 110.801 116.363 110.766 116.083 110.714V107.649C116.363 107.719 116.994 107.719 117.309 107.719C119.078 107.719 120.006 106.983 120.584 105.092C120.584 105.056 120.917 103.971 120.917 103.953L114.262 85.5107H118.36L123.036 100.503H123.089L127.765 85.5107H131.758L124.84 104.899C123.264 109.365 121.46 110.801 117.624 110.801Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M104.574 104.25C101.877 104.25 99.7927 102.937 98.6543 100.8H98.5842V103.935H94.9062V78.6621H98.6893V88.6451H98.7769C99.8803 86.5084 101.982 85.2124 104.592 85.2124C109.233 85.2124 112.263 88.8903 112.263 94.7225C112.263 100.572 109.25 104.25 104.574 104.25ZM103.558 88.4C100.686 88.4 98.6718 90.922 98.6718 94.7225C98.6718 98.5581 100.686 101.045 103.558 101.045C106.536 101.045 108.375 98.6282 108.375 94.7225C108.375 90.8519 106.536 88.4 103.558 88.4Z"
                        fill="#0F5744"
                      />
                      <path
                        d="M74.4969 37.2446C74.4969 50.9556 63.3965 62.0573 49.6873 62.0573H37.257C37.257 80.3727 22.4053 95.2262 4.0923 95.2262H0V37.2446C0 16.6782 16.6761 0 37.2399 0C57.8037 0 74.4799 16.6782 74.4799 37.2446H74.4969Z"
                        fill="url(#paint0_linear_1665_4477)"
                      />
                      <path
                        style={{ mixBlendMode: "multiply" }}
                        d="M37.2556 62.0645C37.2556 62.0645 0.015625 58.4322 0.015625 95.2334H4.10793C22.421 95.2334 37.2726 80.3799 37.2726 62.0645H37.2556Z"
                        fill="url(#paint1_linear_1665_4477)"
                      />
                      <path
                        style={{ mixBlendMode: "multiply" }}
                        d="M0 95.2271H4.0923C4.87666 95.2271 5.66101 95.193 6.44537 95.1418C8.13344 64.9744 37.257 62.0753 37.257 62.0753C37.257 62.0753 0.0170484 58.4429 0.0170484 95.2442L0 95.2271Z"
                        fill="url(#paint2_linear_1665_4477)"
                      />
                      <path
                        style={{ mixBlendMode: "multiply" }}
                        d="M0 95.2334H2.14845C3.15448 61.0072 37.2399 62.0645 37.2399 62.0645C37.2399 62.0645 0 58.4322 0 95.2334Z"
                        fill="url(#paint3_linear_1665_4477)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_1665_4477"
                          x1="-28.1687"
                          y1="101.57"
                          x2="52.8693"
                          y2="18.769"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0.15" stopColor="#0F5744" />
                          <stop offset="1" stopColor="#00E9A8" />
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear_1665_4477"
                          x1="33.1462"
                          y1="48.2513"
                          x2="6.76256"
                          y2="101.66"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0.15" stopColor="#0F5744" />
                          <stop offset="1" stopColor="#00E9A8" />
                        </linearGradient>
                        <linearGradient
                          id="paint2_linear_1665_4477"
                          x1="49.0053"
                          y1="32.8287"
                          x2="-4.51"
                          y2="101.888"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0.15" stopColor="#0F5744" />
                          <stop offset="1" stopColor="#00E9A8" />
                        </linearGradient>
                        <linearGradient
                          id="paint3_linear_1665_4477"
                          x1="49.0053"
                          y1="32.835"
                          x2="-4.50999"
                          y2="101.895"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0.15" stopColor="#0F5744" />
                          <stop offset="1" stopColor="#00E9A8" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <div className="flex justify-center mb-3">
                      <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-100 to-teal-100 border border-green-200">
                        <svg
                          className="h-4 w-4 mr-2"
                          width="75"
                          height="96"
                          viewBox="0 0 75 96"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M333.557 75.1505C333.4 75.1505 329.659 75.1505 329.502 75.1191V66.6944C329.659 66.7258 331.734 66.7572 331.922 66.7572C335.412 66.7572 337.078 65.877 337.361 63.8023C337.361 63.6137 337.486 62.7649 337.424 62.5449L325.887 28.3115H338.178L344.371 54.183H344.559L350.721 28.3115H362.478L351.538 62.4506C348.615 71.6297 343.585 75.1505 333.557 75.1505Z"
                            fill="#00E9A8"
                          />
                          <path
                            d="M306.06 55.3834C309.455 55.3834 312.41 53.3086 312.41 50.2594V47.9017L306.186 48.3103C303.262 48.5304 301.628 49.8821 301.628 51.894C301.628 54.0631 303.419 55.3834 306.06 55.3834ZM302.099 63.0536C295.749 63.0536 290.594 58.8413 290.594 52.6485C290.594 46.2356 295.529 42.6519 304.394 42.1175L312.41 41.646V39.697C312.41 36.9621 310.461 35.4532 307.538 35.4532C304.52 35.4532 302.602 37.025 302.288 39.1312H292.26C292.574 32.121 298.547 27.5 308.103 27.5C317.251 27.5 323.318 32.1839 323.318 39.0683V62.5507H312.473V57.6781H312.253C310.398 61.1361 306.217 63.0536 302.099 63.0536Z"
                            fill="#00E9A8"
                          />
                          <path
                            d="M253.395 17.1855H273.042C282.787 17.1855 289.388 23.5041 289.388 33.2491C289.388 42.9313 282.441 49.2498 272.35 49.2498H264.931V62.5471H253.395V17.1855ZM264.931 26.0189V40.5736H269.93C274.834 40.5736 277.726 38.0273 277.726 33.2806C277.726 28.5652 274.834 26.0189 269.993 26.0189H264.931Z"
                            fill="#00E9A8"
                          />
                          <path
                            d="M213.652 62.5471V17.1855H224.56V34.758H224.781C226.541 30.1684 230.282 27.6536 235.5 27.6536C243.139 27.6536 247.383 32.4947 247.383 40.5736V62.5471H236.255V42.9941C236.255 39.1276 234.337 36.7385 230.659 36.7385C226.918 36.7385 224.812 39.4105 224.812 43.2456V62.5471H213.652Z"
                            fill="#0F5744"
                          />
                          <path
                            d="M209.35 41.8346H199.102C198.725 38.2824 196.587 35.9876 193.349 35.9876C189.42 35.9876 187.125 39.3826 187.125 45.4183C187.125 51.5168 189.42 54.9118 193.349 54.9118C196.619 54.9118 198.725 52.8056 199.134 49.2534H209.35C209.067 57.9611 202.874 63.368 193.224 63.368C182.504 63.368 175.84 56.6408 175.84 45.4183C175.84 34.3215 182.504 27.5 193.161 27.5C202.812 27.5 209.067 33.2527 209.35 41.8346Z"
                            fill="#0F5744"
                          />
                          <path
                            d="M155.219 20.6455H166.378V28.3158H172.54V36.5205H166.378V50.8865C166.378 53.2442 167.636 54.4387 170.308 54.4387C171.188 54.4387 171.848 54.4073 172.508 54.2816V62.2033C171.44 62.4234 169.805 62.612 167.636 62.612C158.74 62.612 155.219 59.8771 155.219 53.1499V36.5205H150.566V28.3158H155.219V20.6455Z"
                            fill="#0F5744"
                          />
                          <path
                            d="M135.852 62.5484V28.3151H146.981V62.5484H135.852ZM141.417 25.5488C138.179 25.5488 135.695 23.1597 135.695 20.079C135.695 16.9983 138.179 14.6406 141.417 14.6406C144.654 14.6406 147.106 16.9983 147.106 20.079C147.106 23.1597 144.654 25.5488 141.417 25.5488Z"
                            fill="#0F5744"
                          />
                          <path
                            d="M95.3984 17.1855H115.046C124.791 17.1855 131.392 23.5041 131.392 33.2491C131.392 42.9313 124.445 49.2498 114.354 49.2498H106.935V62.5471H95.3984V17.1855ZM106.935 26.0189V40.5736H111.934C116.837 40.5736 119.73 38.0273 119.73 33.2806C119.73 28.5652 116.837 26.0189 111.996 26.0189H106.935Z"
                            fill="#0F5744"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-green-800">
                          {invoice
                            ? `PitchPay Link for Invoice ${invoice.invoiceNumber}`
                            : "Generate a PitchPay Payment Link"}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Generate a secure payment link for{" "}
                        <strong>{customer?.displayName}</strong> to bank
                        transfer using PitchPay.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <label
                    htmlFor="amount"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Amount
                  </label>
                  <div className="mt-2">
                    <div className="relative rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">£</span>
                      </div>
                      <input
                        type="text"
                        name="amount"
                        id="amount"
                        className="block w-full rounded-md border-0 py-1.5 pl-7 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-martEye-500 sm:text-sm sm:leading-6"
                        placeholder="0.00"
                        value={amountInput}
                        onChange={(e) => handleAmountChange(e.target.value)}
                      />
                    </div>
                    {customerBalanceInCents > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        Customer balance:{" "}
                        {new Intl.NumberFormat("en-GB", {
                          style: "currency",
                          currency: currency,
                        }).format(customerBalanceInCents / 100)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-5">
                  <label
                    htmlFor="link"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Payment Link
                  </label>
                  <div className="mt-2">
                    <div className="flex rounded-md shadow-sm">
                      <input
                        type="text"
                        name="link"
                        id="link"
                        className="block w-full rounded-l-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-martEye-500 sm:text-sm sm:leading-6"
                        value={paymentLink}
                        readOnly
                      />
                      <button
                        type="button"
                        className={`relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-gray-300 transition-colors duration-200 ${
                          copied
                            ? "bg-green-500 hover:bg-green-600"
                            : "bg-martEye-500 hover:bg-martEye-600"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard();
                        }}
                        disabled={copied}
                      >
                        {copied ? (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    onClick={closeDialog}
                  >
                    Close
                  </button>
                </div>

                {/* Simple Status Indicator
                <div className="mt-4 flex items-center justify-center">
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-2 w-2 ${paymentStatus.color} rounded-full`}
                    ></div>
                    <span className="text-xs text-gray-500">
                      {paymentStatus.text}
                    </span>
                  </div>
                </div> */}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
